/**
 * Client-side caching utility for API responses
 * Supports TTL, pattern invalidation, and local storage persistence
 */

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
  cachedAt: number;
}

class CacheManager {
  private memoryCache: Map<string, CacheEntry<unknown>> = new Map();

  private readonly defaultTTL = 5 * 60 * 1000; // 5 minutes

  private readonly maxCacheSize = 100; // Prevent memory bloat

  /**
   * Get cached data or fetch and cache it
   */
  async getOrFetch<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttl: number = this.defaultTTL
  ): Promise<T> {
    const cached = this.get<T>(key);
    if (cached !== null) {
      return cached;
    }

    const data = await fetcher();
    this.set(key, data, ttl);
    return data;
  }

  /**
   * Get cached data by key
   */
  get<T>(key: string): T | null {
    const entry = this.memoryCache.get(key);

    if (!entry) {
      return null;
    }

    if (Date.now() > entry.expiresAt) {
      this.memoryCache.delete(key);
      return null;
    }

    // entry.data is stored as unknown; cast to the requested generic T on return
    return entry.data as T;
  }

  /**
   * Set cache entry
   */
  set<T>(key: string, data: T, ttl: number = this.defaultTTL): void {
    // Evict oldest entries if cache is full
    if (this.memoryCache.size >= this.maxCacheSize) {
      const oldestKey = this.memoryCache.keys().next().value;
      if (oldestKey) {
        this.memoryCache.delete(oldestKey);
      }
    }

    this.memoryCache.set(key, {
      data,
      expiresAt: Date.now() + ttl,
      cachedAt: Date.now(),
    });
  }

  /**
   * Invalidate cache entries matching a pattern
   */
  invalidatePattern(pattern: string): number {
    let count = 0;
    Array.from(this.memoryCache.keys()).forEach(key => {
      if (key.includes(pattern)) {
        this.memoryCache.delete(key);
        count += 1;
      }
    });
    return count;
  }

  /**
   * Invalidate specific cache key
   */
  invalidate(key: string): boolean {
    return this.memoryCache.delete(key);
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.memoryCache.clear();
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    Array.from(this.memoryCache.values()).forEach(entry => {
      if (now > entry.expiresAt) {
        expiredEntries += 1;
      } else {
        validEntries += 1;
      }
    });

    return {
      total: this.memoryCache.size,
      valid: validEntries,
      expired: expiredEntries,
      maxSize: this.maxCacheSize,
    };
  }

  /**
   * Clean up expired entries
   */
  cleanup(): number {
    const now = Date.now();
    let removed = 0;

    this.memoryCache.forEach((entry, key) => {
      if (now > entry.expiresAt) {
        this.memoryCache.delete(key);
        removed += 1;
      }
    });

    return removed;
  }
}

// Export singleton instance
export const cache = new CacheManager();

// Auto-cleanup every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(
    () => {
      const removed = cache.cleanup();
      if (removed > 0) {
        console.log(`[Cache] Cleaned up ${removed} expired entries`);
      }
    },
    5 * 60 * 1000
  );
}
